import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, LogOut, Search, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Logo } from "@/components/Primitives";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { LeadDetail } from "@/components/admin/LeadDetail";
import { errText, http, money } from "@/lib/site";

const STATUSES = ["new", "contacted", "proposal", "won", "lost"];

const toCsv = (rows) => {
  const cols = ["created_at", "business_name", "contact_name", "email", "phone", "business_type", "monthly_volume", "current_rate", "services", "statement_review", "status", "message"];
  const esc = (v) => `"${String(Array.isArray(v) ? v.join("; ") : v ?? "").replace(/"/g, '""')}"`;
  return [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
};

const Stat = ({ label, value, testid }) => (
  <div className="rounded-2xl bg-white border border-slate-200 p-5">
    <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-600">{label}</p>
    <p className="mt-2 font-display text-3xl font-semibold" data-testid={testid}>{value}</p>
  </div>
);

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("vectis_admin_token"));
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const logout = () => {
    localStorage.removeItem("vectis_admin_token");
    setToken(null);
  };
  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/leads", auth);
      setLeads(Array.isArray(data) ? data : []);
    } catch (e) {
      if (e?.response?.status === 401) logout();
      else toast.error(errText(e));
    }
  }, [auth]);
  useEffect(() => {
    if (token) load();
  }, [token, load]);

  if (!token) return <AdminLogin onAuthed={setToken} />;

  const setStatus = async (id, status) => {
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    try { await http.patch(`/admin/leads/${id}`, { status }, auth); } catch (e) { toast.error(errText(e)); load(); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try { await http.delete(`/admin/leads/${id}`, auth); setLeads((ls) => ls.filter((l) => l.id !== id)); } catch (e) { toast.error(errText(e)); }
  };
  const exportCsv = () => {
    const url = URL.createObjectURL(new Blob([toCsv(leads)], { type: "text/csv" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: "vectis-leads.csv" });
    a.click();
    URL.revokeObjectURL(url);
  };

  const shown = leads.filter((l) => (filter === "all" || l.status === filter) &&
    `${l.business_name} ${l.contact_name} ${l.email}`.toLowerCase().includes(q.toLowerCase()));
  const pipeline = leads.reduce((s, l) => s + (l.monthly_volume || 0), 0);

  return (
    <div className="min-h-screen bg-[var(--paper)]" data-testid="admin-dashboard">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="admin-home-link"><Logo /></Link>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm font-medium hover:text-[var(--cobalt)]"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <h1 className="font-display text-4xl font-semibold tracking-tight">Leads</h1>
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Total leads" value={leads.length} testid="stat-total" />
          <Stat label="New" value={leads.filter((l) => l.status === "new").length} testid="stat-new" />
          <Stat label="Monthly volume pipeline" value={money(pipeline)} testid="stat-pipeline" />
          <Stat label="Statement reviews" value={leads.filter((l) => l.statement_review).length} testid="stat-reviews" />
        </div>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input className="pl-9 h-11 rounded-xl bg-white" placeholder="Search business, name or email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-search" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-11 rounded-xl bg-white sm:w-44" data-testid="admin-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["all", ...STATUSES].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <button onClick={exportCsv} data-testid="admin-export-csv" className="h-11 inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--ink)] text-white px-5 text-sm font-semibold hover:bg-[var(--cobalt)] transition-colors"><Download className="h-4 w-4" /> Export CSV</button>
        </div>
        <div className="mt-6 rounded-2xl bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]" data-testid="admin-leads-table">
            <thead className="text-left text-slate-600 border-b border-slate-200">
              <tr>{["Received", "Business", "Contact", "Volume", "Services", "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr>
            </thead>
            <tbody>
              {shown.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-600" data-testid="admin-empty">No leads yet.</td></tr>}
              {shown.map((l) => (
                <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600">{new Date(l.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3"><button onClick={() => setOpen(l)} className="font-semibold hover:text-[var(--cobalt)] text-left" data-testid={`lead-open-${l.id}`}>{l.business_name}</button>{l.statement_review && <span className="ml-2 text-[10px] font-mono uppercase bg-blue-50 text-[var(--cobalt)] rounded px-1.5 py-0.5">Review</span>}</td>
                  <td className="px-4 py-3"><div>{l.contact_name}</div><div className="text-slate-600 text-xs">{l.email}</div></td>
                  <td className="px-4 py-3 font-mono">{money(l.monthly_volume)}</td>
                  <td className="px-4 py-3 text-slate-600">{(l.services || []).join(", ") || "—"}</td>
                  <td className="px-4 py-3">
                    <Select value={l.status} onValueChange={(v) => setStatus(l.id, v)}>
                      <SelectTrigger className="h-9 w-32 rounded-lg capitalize" data-testid={`lead-status-${l.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3"><button onClick={() => remove(l.id)} data-testid={`lead-delete-${l.id}`} aria-label="Delete lead" className="p-2 text-slate-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
      <LeadDetail lead={open} onClose={() => setOpen(null)} />
    </div>
  );
}
